import React from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import type { Job, Grower } from "../../../../../../models";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { CREATE_NEW_GROWER } from "../../../../../../utilities/constants";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { ColumnContext } from "../standard-column-types";
import { useGrowerColumnViewModel } from "./Grower.column.vm";
import { useValidationErrors } from "../../shared/hooks/ValidationErrorsContext";

type GrowerOption = Grower | { id: string; name: string };

const filter = createFilterOptions<GrowerOption>();

export interface GrowerColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly Grower column component.
 * Allows selection of an existing grower or creation of a new one.
 */
export const GrowerColumnMobile: React.FC<GrowerColumnMobileProps> = ({
	job,
	columnContext,
	required = false,
	disabled = false,
	label,
}) => {
	const vm = useGrowerColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();

	const allGrowers = vm.getAllGrowers();
	const selectedGrower = vm.getSelectedGrower(job);
	const errorKey = getValidationErrorKey("grower", job.id);

	const handleChange = (_event: React.SyntheticEvent, value: string | GrowerOption | null, reason: string) => {
		if (disabled) return;

		// Ignore blur events
		if (reason === "blur") return;

		const newJobs = [...columnContext.jobs];
		const jobIndex = newJobs.findIndex((j) => j.id === job.id);
		if (jobIndex === -1) return;

		const newJob = { ...newJobs[jobIndex] };

		if (typeof value === "string") {
			const newValue = value.startsWith("New Grower:") ? value.replace("New Grower: ", "") : value;

			if (newValue !== "") {
				const existingGrower = allGrowers.find((g) => g.name.toLowerCase() === newValue.toLowerCase());

				if (existingGrower) {
					newJob.importGrowerName = existingGrower.name;
					newJob.growerIds = [existingGrower.id];
					newJob.growerPhoneNumber = existingGrower.phoneNumber || "";
				} else {
					newJob.importGrowerName = newValue;
					newJob.growerIds = [CREATE_NEW_GROWER];
					newJob.growerPhoneNumber = "";
				}
			}
		} else if (value && value.id === CREATE_NEW_GROWER) {
			const name = value.name.replace("New Grower: ", "");
			const existingGrower = allGrowers.find((g) => g.name.toLowerCase() === name.toLowerCase());

			if (existingGrower) {
				newJob.importGrowerName = existingGrower.name;
				newJob.growerIds = [existingGrower.id];
				newJob.growerPhoneNumber = existingGrower.phoneNumber || "";
			} else {
				newJob.importGrowerName = name;
				newJob.growerIds = [CREATE_NEW_GROWER];
				newJob.growerPhoneNumber = "";
			}
		} else if (value && value.id !== CREATE_NEW_GROWER) {
			const grower = allGrowers.find((g) => g.id === value.id);
			newJob.importGrowerName = value.name;
			newJob.growerIds = [value.id];
			newJob.growerPhoneNumber = (grower as Grower)?.phoneNumber || "";
		} else if (value === null && reason === "clear") {
			newJob.importGrowerName = "";
			newJob.growerIds = [];
			newJob.growerPhoneNumber = "";
		} else {
			return;
		}

		newJobs[jobIndex] = newJob;
		columnContext.setJobs(newJobs);
		columnContext.validateJobs([newJob], { updateState: true, clearPrevious: false });
	};

	return (
		<Autocomplete
			size="small"
			fullWidth
			freeSolo
			disabled={disabled}
			value={selectedGrower}
			options={allGrowers as GrowerOption[]}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			getOptionLabel={(option) => {
				if (typeof option === "string") return option;
				return option?.name ?? "";
			}}
			filterOptions={(options, params) => {
				const filtered = filter(options, params);

				if (
					params.inputValue !== "" &&
					!allGrowers.some((grower) => grower.name.toLowerCase() === params.inputValue.toLowerCase())
				) {
					filtered.push({ id: CREATE_NEW_GROWER, name: `New Grower: ${params.inputValue}` });
				}

				return filtered;
			}}
			onChange={handleChange}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || columnDisplayNames.grower}
					required={required}
					error={!!validationErrors?.[errorKey]}
					helperText={validationErrors?.[errorKey]}
				/>
			)}
		/>
	);
};
